import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { UseOfForceReportModel, UseOfForceUploadedFile } from '../../api-service/clients';
import { formatCpr } from '../../utilities';
import { attachUseOfForceReportActionPlan, getUseOfForceReportActionPlan, removeUseOfForceReportActionPlan } from './use-of-force-report-api';
import { UseOfForceAttachmentTable } from '../citizen/serviceprovider/use-of-force/use-of-force-attachment-table';

interface UseOfForceReportActionPlansProps {
  serviceProviderUnitId: string;
  report: UseOfForceReportModel;
  onChanged: () => void;
}

// BEK 1411 § 19: a copy of the citizen's handleplan goes with the report, when one has been made.
export function UseOfForceReportActionPlans(props: UseOfForceReportActionPlansProps) {
  const { serviceProviderUnitId, report, onChanged } = props;
  const [busyCitizenId, setBusyCitizenId] = useState<string>();
  const citizens = [...new Map(report.incidents.map(incident => [incident.citizenId, incident])).values()];

  async function attach(citizenId: string, files: UseOfForceUploadedFile[]) {
    setBusyCitizenId(citizenId);
    try {
      for (const file of files) await attachUseOfForceReportActionPlan(serviceProviderUnitId, report.id, citizenId, file);
      onChanged();
    } finally {
      setBusyCitizenId(undefined);
    }
  }

  async function remove(actionPlanIds: string[]) {
    for (const actionPlanId of actionPlanIds) await removeUseOfForceReportActionPlan(serviceProviderUnitId, report.id, actionPlanId);
    onChanged();
  }

  async function open(actionPlanId: string) {
    const file = await getUseOfForceReportActionPlan(serviceProviderUnitId, report.id, actionPlanId);
    window.open(URL.createObjectURL(file), '_blank');
  }

  if (citizens.length === 0) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2">Kopi af handleplan</Typography>
      <Typography variant="body2" color="text.secondary">
        Vedlæg borgerens handleplan efter servicelovens § 141, hvis der er udarbejdet en.
      </Typography>
      {citizens.map(citizen => (
        <Box key={citizen.citizenId} sx={{ mt: 1 }}>
          <Typography variant="body2">
            {citizen.citizenName ?? '-'}
            {citizen.citizenCpr ? ` (${formatCpr(citizen.citizenCpr)})` : ''}
          </Typography>
          <UseOfForceAttachmentTable
            attachments={report.actionPlans.filter(actionPlan => actionPlan.citizenId === citizen.citizenId)}
            isLoading={busyCitizenId === citizen.citizenId}
            onAttach={files => attach(citizen.citizenId, files)}
            onRemove={remove}
            onOpen={open}
          />
        </Box>
      ))}
    </Box>
  );
}
